import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, UserCheck, QrCode, CheckCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import QRScannerModal from '../components/checkin/QRScannerModal';
import QRScannerModalV2 from '../components/checkin/QRScannerModalV2';
import { attendeeAPI, eventAPI } from '../api/endpoints';
import { getAttendeeName } from '../utils/attendeeHelpers';
import { useToastContext } from '../context/ToastContext';
import { useEvent } from '../context/EventContext';
import EventSelector from '../components/common/EventSelector';

const CheckIn = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const { selectedEvent, setSelectedEvent } = useEvent();
  const [searchTerm, setSearchTerm] = useState('');
  const [recentCheckIns, setRecentCheckIns] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showNativeScanner, setShowNativeScanner] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);

  // Fetch events with attendee counts
  const { data: eventsData } = useQuery({
    queryKey: ['events-with-counts'],
    queryFn: async () => {
      const response = await eventAPI.getAll();
      const events = response.data.events || [];
      
      // Fetch attendee counts for all events in parallel
      const eventCountPromises = events.map(async (event) => {
        try {
          const attendeesResponse = await attendeeAPI.getAll(event.id);
          return {
            ...event,
            realAttendeeCount: attendeesResponse.data.attendees?.length || 0
          };
        } catch (error) {
          console.warn(`Failed to fetch attendees for event ${event.id}:`, error);
          return {
            ...event,
            realAttendeeCount: event.attendeeCount || 0
          };
        }
      });
      
      const eventsWithCounts = await Promise.all(eventCountPromises);
      
      return { events: eventsWithCounts };
    },
  });

  // Fetch all attendees for client-side search
  const { data: attendeesData, isLoading: loadingAttendees } = useQuery({
    queryKey: ['all-attendees'],
    queryFn: async () => {
      const response = await attendeeAPI.getAll({ limit: 1000 });
      return response.data;
    },
  });

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationFn: (id) => attendeeAPI.checkIn(id),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries(['all-attendees']);
      const attendee = allAttendees.find(a => a.id === variables);
      const attendeeName = attendee ? getAttendeeName(attendee) : 'Attendee';
      setRecentCheckIns(prev => [{ 
        id: variables, 
        name: attendeeName,
        time: new Date() 
      }, ...prev.slice(0, 9)]);
      setSearchTerm('');
      toast.success(`${attendeeName} checked in successfully!`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to check in attendee');
    },
  });

  // Helper function to get field value
  const getFieldValue = (attendee, fieldName) => {
    if (!attendee.data || !Array.isArray(attendee.data)) return '';
    const field = attendee.data.find(d => d.field?.field_name === fieldName);
    return field?.value || '';
  };

  // Client-side search
  const allAttendees = attendeesData?.attendees || [];
  const filteredAttendees = allAttendees.filter(attendee => {
    if (!searchTerm || searchTerm.length < 2) return false;
    
    const search = searchTerm.toLowerCase();
    const name = getAttendeeName(attendee).toLowerCase();
    const email = getFieldValue(attendee, 'email').toLowerCase();
    const phone = getFieldValue(attendee, 'phone').toLowerCase();
    const company = getFieldValue(attendee, 'company').toLowerCase();
    
    return name.includes(search) || 
           email.includes(search) || 
           phone.includes(search) ||
           company.includes(search);
  }).slice(0, 10); // Limit to 10 results

  const handleCheckIn = (attendeeId) => {
    checkInMutation.mutate(attendeeId);
  };

  const handleQRScan = async (qrCode) => {
    setScannerLoading(true);
    try {
      let attendeeId = null;
      let eventId = null;
      
      // Check if QR code is a URL (from email) or a simple token
      if (qrCode.includes('/attendee/')) {
        // Extract attendee ID and event ID from URL
        // Format: http://localhost:3000/attendee/{attendee_id}?event={event_id}
        const urlMatch = qrCode.match(/\/attendee\/([^?]+)/);
        const eventMatch = qrCode.match(/[?&]event=([^&]+)/);
        
        if (urlMatch && eventMatch) {
          attendeeId = urlMatch[1];
          eventId = eventMatch[1];
          
          // QR Code URL detected and parsed successfully
        } else {
          toast.error('Invalid QR code format');
          setScannerLoading(false);
          return;
        }
      } else {
        // Legacy format - try the old verifyQR endpoint
        try {
          const response = await attendeeAPI.verifyQR(qrCode);
          const attendee = response.data.data;
          attendeeId = attendee?.id;
          eventId = attendee?.event_id;
        } catch (verifyError) {
          toast.error('Invalid QR code');
          setScannerLoading(false);
          return;
        }
      }

      if (!attendeeId) {
        toast.error('Could not extract attendee information from QR code');
        setScannerLoading(false);
        return;
      }

      // Get attendee details using the public endpoint (no auth required)
      let attendee;
      try {
        const response = await attendeeAPI.getPublicDetailNoAuth(attendeeId, eventId);
        
        // The getPublicDetailNoAuth returns { attendee: ... } structure
        attendee = response.attendee || response.data?.attendee || response;
      } catch (error) {
        console.error('📱 Public API Error:', error);
        toast.error(`Attendee not found: ${error.message}`);
        setScannerLoading(false);
        return;
      }

      if (!attendee) {
        console.error('📱 No attendee data in response');
        toast.error('Attendee not found - no data returned');
        setScannerLoading(false);
        return;
      }

      // Check if already checked in
      if (attendee.check_in_status === 'checked_in') {
        toast.warning(`${getAttendeeName(attendee)} is already checked in!`);
        setScannerLoading(false);
        // Keep scanner open for next scan
        return;
      }

      // Perform check-in using the same API as the Attendees page
      await attendeeAPI.checkIn(eventId, attendeeId);
      
      // Update recent check-ins
      setRecentCheckIns(prev => [{ 
        id: attendeeId, 
        name: getAttendeeName(attendee),
        time: new Date() 
      }, ...prev.slice(0, 9)]);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries(['all-attendees']);
      
      toast.success(`${getAttendeeName(attendee)} checked in successfully!`);
      // Keep scanner open for next scan - don't close automatically
    } catch (error) {
      console.error('QR scan error:', error);
      toast.error(error.response?.data?.error || 'Failed to check in attendee');
    } finally {
      setScannerLoading(false);
    }
  };

  const attendees = filteredAttendees;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Check-In Station</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Quick check-in for event attendees</p>
        </div>

        {/* Event Selector */}
        <EventSelector 
          selectedEvent={selectedEvent}
          onEventChange={setSelectedEvent}
          className="w-full max-w-full sm:max-w-md"
          attendeeCount={attendees.length}
          eventsWithCounts={eventsData?.events || []}
        />

        {/* Attendee count display */}
        {selectedEvent && attendees.length > 0 && (
          <div className="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg w-full sm:max-w-md">
            <span className="font-medium">{attendees.length}</span> attendee{attendees.length !== 1 ? 's' : ''} found for check-in
          </div>
        )}

        {/* Search */}
        <Card>
          <Card.Content>
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 sm:pl-14 pr-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              {/* Search Results */}
              {loadingAttendees && searchTerm.length >= 2 && (
                <div className="text-center py-4 text-gray-500">Loading attendees...</div>
              )}

              {searchTerm.length >= 2 && !loadingAttendees && attendees.length > 0 && (
                <div className="space-y-2">
                  {attendees.map((attendee) => (
                    <div
                      key={attendee.id}
                      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border-2 rounded-lg transition-all gap-3 sm:gap-4 ${
                        attendee.check_in_status === 'checked_in' 
                          ? 'border-green-200 bg-green-50' 
                          : 'border-blue-200 bg-blue-50 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">
                          {getAttendeeName(attendee)}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1 truncate">{attendee.event?.name}</p>
                        <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 mt-2">
                          {getFieldValue(attendee, 'email') && (
                            <span className="text-xs text-gray-500 truncate">
                              📧 {getFieldValue(attendee, 'email')}
                            </span>
                          )}
                          {getFieldValue(attendee, 'phone') && (
                            <span className="text-xs text-gray-500 truncate">
                              📱 {getFieldValue(attendee, 'phone')}
                            </span>
                          )}
                        </div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold mt-2 ${
                          attendee.check_in_status === 'checked_in' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          <span className="hidden sm:inline">{attendee.check_in_status === 'checked_in' ? '✓ Already Checked In' : '⏳ Pending Check-In'}</span>
                          <span className="sm:hidden">{attendee.check_in_status === 'checked_in' ? '✓ Checked In' : '⏳ Pending'}</span>
                        </span>
                      </div>
                      {attendee.check_in_status !== 'checked_in' ? (
                        <Button
                          onClick={() => handleCheckIn(attendee.id)}
                          className="gap-2 w-full sm:w-auto"
                          loading={checkInMutation.isPending && checkInMutation.variables === attendee.id}
                          size="lg"
                        >
                          <UserCheck className="h-5 w-5" />
                          <span className="hidden xs:inline">Check In</span>
                          <span className="xs:hidden">Check</span>
                        </Button>
                      ) : (
                        <div className="flex justify-center sm:justify-end text-green-600">
                          <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {searchTerm.length >= 2 && !loadingAttendees && attendees.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Search className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>No attendees found matching "{searchTerm}"</p>
                  <p className="text-sm mt-1">Try searching by name, email, phone, or company</p>
                </div>
              )}

              {searchTerm.length > 0 && searchTerm.length < 2 && (
                <div className="text-center py-4 text-gray-400 text-sm">
                  Type at least 2 characters to search
                </div>
              )}
            </div>
          </Card.Content>
        </Card>

        {/* QR Code Scanner */}
        <Card>
          <Card.Content className="text-center py-8">
            <QrCode className="h-16 w-16 text-primary-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              QR Code Scanner
            </h3>
            <p className="text-gray-600 mb-4">
              Scan attendee QR codes for instant check-in<br/>
              <span className="text-sm text-primary-600">Choose your preferred scanner</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <Button 
                onClick={() => setShowScanner(true)} 
                className="gap-2 w-full sm:w-auto" 
                variant="secondary"
              >
                <QrCode className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden xs:inline">Standard Scanner</span>
                <span className="xs:hidden">Standard</span>
              </Button>
              <Button 
                onClick={() => setShowNativeScanner(true)} 
                className="gap-2 w-full sm:w-auto"
              >
                <QrCode className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden xs:inline">Enhanced Scanner</span>
                <span className="xs:hidden">Enhanced</span>
              </Button>
            </div>
          </Card.Content>
        </Card>

        {/* Recent Check-Ins */}
        {recentCheckIns.length > 0 && (
          <Card>
            <Card.Content>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Recent Check-Ins
                </h3>
                <button
                  onClick={() => setRecentCheckIns([])}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
              <div className="space-y-2">
                {recentCheckIns.map((checkIn, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <span className="text-sm font-medium text-green-900">{checkIn.name}</span>
                        <span className="text-xs text-green-600 ml-2">checked in successfully</span>
                      </div>
                    </div>
                    <span className="text-xs text-green-600">{checkIn.time.toLocaleTimeString()}</span>
                  </div>
                ))}
              </div>
            </Card.Content>
          </Card>
        )}
      </div>

      {/* QR Scanner Modals */}
      <QRScannerModal
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleQRScan}
        loading={scannerLoading}
      />
      
      <QRScannerModalV2
        isOpen={showNativeScanner}
        onClose={() => setShowNativeScanner(false)}
        onScan={handleQRScan}
        loading={scannerLoading}
      />
    </Layout>
  );
};

export default CheckIn;
